import type {
  CommercialFineType,
  EmailTemplateArea,
  EmailTemplatePlaceholderGroup,
  EmailTemplatePlaceholder,
  PropertyCategory,
  PropertySubtype,
} from '@immo/api-contract';
import type { EnumTranslation } from '../types';

// Shared Immobilientyp labels (IMM-432), keyed by the en-US taxonomy tokens.
// `satisfies EnumTranslation<…>` forces a label for every token, so adding a
// category or subtype in `@immo/api-contract` fails the build until it is
// translated here. Lives in `common` so leads, properties and emails render
// the same type identically.
const IMMOBILIENTYP_CATEGORY_LABELS = {
  APARTMENT: 'Wohnung',
  HOUSE: 'Haus',
  LAND: 'Grundstück',
  COMMERCIAL: 'Gewerbe',
  GARAGE_PARKING: 'Garage / Stellplatz',
} as const satisfies EnumTranslation<PropertyCategory>;

const COMMERCIAL_FINE_TYPE_LABELS = {
  LOFT: 'Loft',
  STUDIO: 'Atelier',
  OFFICE: 'Büro',
  OFFICE_FLOOR: 'Büroetage',
  OFFICE_BUILDING: 'Bürohaus',
  OFFICE_CENTRE: 'Bürozentrum',
  OFFICE_STORAGE_BUILDING: 'Büro- und Lagergebäude',
  SURGERY: 'Praxis',
  SURGERY_FLOOR: 'Praxisetage',
  SURGERY_BUILDING: 'Praxishaus',
  COMMERCIAL_CENTRE: 'Gewerbezentrum',
  LIVING_AND_COMMERCIAL_BUILDING: 'Wohn- und Geschäftsgebäude',
  OFFICE_AND_COMMERCIAL_BUILDING: 'Büro- und Geschäftsgebäude',
  BAR_LOUNGE: 'Bar/Lounge',
  CAFE: 'Café',
  CLUB_DISCO: 'Club/Diskothek',
  GUESTS_HOUSE: 'Gästehaus',
  TAVERN: 'Gaststätte',
  HOTEL: 'Hotel',
  HOTEL_RESIDENCE: 'Hotelanwesen',
  HOTEL_GARNI: 'Hotel garni',
  PENSION: 'Pension',
  RESTAURANT: 'Restaurant',
  BUNGALOW: 'Ferienbungalow',
  SHOWROOM_SPACE: 'Ausstellungsfläche',
  HALL: 'Halle',
  HIGH_LACK_STORAGE: 'Hochregallager',
  INDUSTRY_HALL: 'Industriehalle',
  INDUSTRY_HALL_WITH_OPEN_AREA: 'Industriehalle mit Freifläche',
  COLD_STORAGE: 'Kühlhaus',
  MULTIDECK_CABINET_STORAGE: 'Kühlregallager',
  STORAGE_WITH_OPEN_AREA: 'Lager mit Freifläche',
  STORAGE_AREA: 'Lagerfläche',
  STORAGE_HALL: 'Lagerhalle',
  SERVICE_AREA: 'Servicefläche',
  SHIPPING_STORAGE: 'Speditionslager',
  REPAIR_SHOP: 'Werkstatt',
  RESIDENCE: 'Anwesen',
  FARM: 'Bauernhof',
  HORSE_FARM: 'Reiterhof',
  VINEYARD: 'Weingut',
  LEISURE_FACILITY: 'Freizeitanlage',
  INDUSTRIAL_AREA: 'Gewerbefläche',
  SPECIAL_ESTATE: 'Spezialobjekt',
} as const satisfies EnumTranslation<CommercialFineType>;

const IMMOBILIENTYP_SUBTYPE_LABELS = {
  FLOOR_APARTMENT: 'Etagenwohnung',
  GROUND_FLOOR_APARTMENT: 'Erdgeschosswohnung',
  ATTIC_APARTMENT: 'Dachgeschosswohnung',
  PENTHOUSE: 'Penthouse',
  MAISONETTE: 'Maisonette',
  LOFT: 'Loft',
  BASEMENT_APARTMENT: 'Souterrain',
  STUDIO_APARTMENT: 'Apartment',
  DETACHED_HOUSE: 'Einfamilienhaus',
  SEMI_DETACHED_HOUSE: 'Doppelhaushälfte',
  TERRACED_HOUSE: 'Reihenhaus',
  VILLA: 'Villa',
  BUNGALOW: 'Bungalow',
  TOWNHOUSE: 'Stadthaus',
  MULTI_FAMILY_HOUSE: 'Mehrfamilienhaus',
  RESIDENTIAL_LAND: 'Wohnen',
  COMMERCIAL_LAND: 'Gewerbe',
  AGRICULTURAL_LAND: 'Land-/Forstwirtschaft',
  OFFICE_PRACTICE: 'Büro/Praxis',
  RETAIL: 'Laden/Einzelhandel',
  HOSPITALITY: 'Gastronomie',
  HOTEL: 'Hotel',
  INDUSTRIAL: 'Halle/Lager/Produktion',
  SPECIAL_PURPOSE: 'Spezialobjekt',
  GARAGE: 'Garage',
  UNDERGROUND_PARKING: 'Tiefgaragenstellplatz',
  OUTDOOR_PARKING: 'Außenstellplatz',
  CARPORT: 'Carport',
  DUPLEX_PARKING: 'Duplex',
} as const satisfies EnumTranslation<PropertySubtype>;

// E-Mail-Vorlagen (IMM-1177). Placeholder names are stored without braces; the
// chip adds `{…}`, because react-intl reads braces in a value as an ICU argument.
const EMAIL_TEMPLATE_AREA_LABELS = {
  BUYER: 'Käufer',
  OWNER: 'Eigentümer',
  GENERAL: 'Allgemein',
} as const satisfies EnumTranslation<EmailTemplateArea>;

const EMAIL_TEMPLATE_PLACEHOLDER_GROUP_LABELS = {
  RECIPIENT: 'Empfänger',
  PROPERTY: 'Objekt',
  PROSPECT: 'Interessent',
  SENDER: 'Absender',
  COMPANY: 'Firma',
} as const satisfies EnumTranslation<EmailTemplatePlaceholderGroup>;

const EMAIL_TEMPLATE_PLACEHOLDER_LABELS = {
  RECIPIENT_GREETING: 'Briefanrede',
  RECIPIENT_SALUTATION: 'Anrede',
  RECIPIENT_FIRST_NAME: 'Vorname',
  RECIPIENT_LAST_NAME: 'Nachname',
  PROPERTY_TITLE: 'Objekttitel',
  PROPERTY_LOCATION: 'Objekt-Ort',
  PROPERTY_ADDRESS: 'Objekt-Adresse',
  PROPERTY_PRICE: 'Kaufpreis',
  PROPERTY_NUMBER: 'Objekt-Nr.',
  PROSPECT_OFFER: 'Angebot',
  PROSPECT_NOTARY_APPOINTMENT: 'Notartermin',
  PROSPECT_PERSONAL_LINK: 'Link zur persönlichen Seite',
  SENDER_FIRST_NAME: 'Absender Vorname',
  SENDER_LAST_NAME: 'Absender Nachname',
  SENDER_PHONE: 'Absender Telefon',
  SENDER_EMAIL: 'Absender E-Mail',
  COMPANY_NAME: 'Firmenname',
} as const satisfies EnumTranslation<EmailTemplatePlaceholder>;

/**
 * Shared user-facing copy: brand, sign-offs, generic action labels. Lives
 * here so emails (backend) and UI (web app) render identically.
 */
export const common = {
  brand: 'immoOS',
  'sign-off': 'Viele Grüße\nDein immoOS-Team',
  copy: {
    'aria-label': '{subject} kopieren',
    success: '{subject} in die Zwischenablage kopiert.',
    subjects: {
      email: 'E-Mail-Adresse',
      phone: 'Telefonnummer',
    },
  },
  'release-notice': {
    title: 'Neue Version verfügbar',
    description: 'Lade die Seite neu, um die neueste Version von immoOS zu verwenden.',
    action: 'Neu laden',
  },
  'outdated-view': {
    title: 'Diese Ansicht ist nicht mehr aktuell',
    action: 'Neu laden',
  },
  modal: {
    close: 'Schließen',
    'confirm-close': {
      title: 'Änderungen verwerfen?',
      description: 'Wenn du jetzt schließt, gehen deine nicht gespeicherten Änderungen verloren.',
      confirm: 'Verwerfen',
      cancel: 'Weiter bearbeiten',
    },
  },
  immobilientyp: {
    category: IMMOBILIENTYP_CATEGORY_LABELS,
    subtype: IMMOBILIENTYP_SUBTYPE_LABELS,
    'commercial-fine-type': COMMERCIAL_FINE_TYPE_LABELS,
  },
  'email-templates': {
    areas: EMAIL_TEMPLATE_AREA_LABELS,
    placeholders: EMAIL_TEMPLATE_PLACEHOLDER_LABELS,
    'placeholder-groups': EMAIL_TEMPLATE_PLACEHOLDER_GROUP_LABELS,
  },
  'buyer-contract-templates': {
    'standard-name': 'Maklervertrag Käufer mit Widerrufsbelehrung – Standard',
    category: 'Maklervertrag Käufer',
    blocks: {
      contract: 'Maklervertrag',
      withdrawalNotice: 'Widerrufsbelehrung',
      withdrawalForm: 'Muster-Widerrufsformular',
    },
    groups: {
      COMPANY: 'Unternehmen',
      BUYER: 'Käufer',
      PROPERTY: 'Objekt',
      NOTICE: 'Belehrung',
      CONTRACT: 'Vertrag',
    },
    fields: {
      companyName: 'Firmenname',
      companyAddress: 'Adresse',
      companyEmail: 'E-Mail',
      companyPhone: 'Telefon',
      buyerName: 'Name',
      commission: 'Provision',
      commissionNote: 'Provisionshinweis',
      propertyTitle: 'Titel',
      propertyCity: 'Ort',
      noticeSentOn: 'Gesendet am',
      concludedOn: 'Geschlossen am',
      withdrawalPeriodEndsOn: 'Widerrufsfrist endet',
    },
    active: 'Aktiv',
    'created-by-immoos': 'immoOS',
  },
} as const;
